import {
  type AggregatedTrackStep,
  type DomesticTrackStep,
  type OrderDetail,
  type OrderItem,
  isSelfCancellable,
  isPayable,
  useCancelOrder,
  useDomesticTrack,
  useOrderDetail,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MiniCashier } from "../components/pay/MiniCashier";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuthToken } from "../lib/auth";
import { miniDisplayCurrency, miniDisplayLocale } from "../lib/locale-currency";

// ─── Progress step mapping (mirrors web order-detail-client) ──────────────────

function progressStep(status: string): { cur: number; total: 6 } {
  const map: Record<string, number> = {
    WAIT_PROCESS: 2,
    PURCHASING: 3,
    CLEAR_BALANCE: 3,
    PURCHASED: 3,
    SELLER_SENT: 3,
    WAIT_STOCK_IN: 3,
    SIGNED: 3,
    STORED: 4,
    WAIT_PICKING: 4,
    WAIT_CONFIRM: 4,
    WAIT_SENT_OVERSEA: 4,
    SHIPPED: 5,
    COMPLETED: 6,
  };
  return { cur: map[status] ?? 0, total: 6 };
}

const STEP_KEYS = [
  { key: "paid", num: 1 },
  { key: "assigned", num: 2 },
  { key: "purchasing", num: 3 },
  { key: "warehouse", num: 4 },
  { key: "shipping", num: 5 },
  { key: "delivered", num: 6 },
] as const;

// ─── Cancel reasons ───────────────────────────────────────────────────────────

const CANCEL_REASON_KEYS = [
  "reason_buy_it",
  "reason_incorrect_info",
  "reason_out_of_stock",
  "reason_unable_pay",
  "reason_other",
] as const;

// ─── Main page ────────────────────────────────────────────────────────────────

export function OrderDetailPage() {
  const params = useParams({ strict: false }) as { orderNo?: string };
  const orderNo = params.orderNo ?? "";
  const { i18n, t } = useTranslation("orders");
  const displayLocale = miniDisplayLocale(i18n.language);
  const displayCurrency = miniDisplayCurrency(i18n.language);
  const authToken = useAuthToken();
  const { data, isLoading, isError } = useOrderDetail(authToken, orderNo);

  const [showCashier, setShowCashier] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (isLoading || (!data && !isError)) {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={`№ ${orderNo}`} back />
        <div
          style={{
            background: "linear-gradient(135deg,var(--color-text),#1A2924)",
            padding: "18px 16px",
          }}
        >
          <TgSkeleton height={20} width={120} radius={999} />
          <div style={{ marginTop: 12 }}>
            <TgSkeleton height={18} width="70%" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 16 }}>
            <TgSkeleton height={32} radius={6} />
            <TgSkeleton height={32} radius={6} />
          </div>
        </div>
        <div className="tg-body" style={{ flex: 1, gap: 10 }}>
          <div className="tg-card">
            <TgSkeleton height={12} width="40%" />
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 10 }}>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} style={{ display: "contents" }}>
                  <TgSkeleton height={20} width={20} radius={999} />
                  {n < 6 && <TgSkeleton height={2} radius={0} />}
                </div>
              ))}
            </div>
          </div>
          <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
            {[1, 2].map((i) => (
              <div key={i} className="tg-prod">
                <TgSkeleton height={52} width={52} radius={10} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  <TgSkeleton height={12} width="80%" />
                  <TgSkeleton height={10} width="40%" />
                </div>
                <TgSkeleton height={14} width={50} />
              </div>
            ))}
          </div>
        </div>
      </TgPage>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────────────
  if (isError || !data) {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={`№ ${orderNo}`} back />
        <div className="tg-body" style={{ flex: 1 }}>
          <TgEmpty
            icon="⚠️"
            title={t("state.error_t")}
            hint={t("state.error_s")}
          />
        </div>
      </TgPage>
    );
  }

  // ── Cashier view (inline, on "Go to Pay") ──────────────────────────────────
  if (showCashier && authToken) {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={`№ ${data.orderNo}`} back={() => setShowCashier(false)} />
        <div className="tg-body" style={{ flex: 1 }}>
          <MiniCashier orderNo={data.orderNo} authToken={authToken} />
        </div>
      </TgPage>
    );
  }

  const { cur } = progressStep(data.status);
  const totalKopecks = data.totalPayAmountRubKopecks || data.orderAmountRubKopecks;
  const canPay = isPayable(data.status);
  const canCancel = isSelfCancellable(data.status);
  // Total QC photos across all items
  const allCheckPhotos = data.items.flatMap((it) => it.checkPhotos ?? []);
  const hasPhotoCheck = data.status === "WAIT_CONFIRM" || data.status === "WAIT_PICKING";

  return (
    <TgPage tabbar={false}>
      {/* Header — borderless so hero card's dark bg is flush */}
      <TgHeader title={`№ ${data.orderNo}`} back borderless />

      {/* ── Dark hero card (design: Order hero dark) ─────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg,var(--color-text),#1A2924)",
          color: "#fff",
          padding: "18px 16px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <span
            style={{
              fontFamily: "monospace",
              fontSize: 11,
              background: "rgba(255,255,255,.12)",
              padding: "3px 10px",
              borderRadius: 999,
            }}
          >
            {data.orderNo}
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              fontWeight: 700,
              background: "rgba(255,255,255,.15)",
              padding: "3px 10px",
              borderRadius: 999,
            }}
          >
            {data.statusName}
          </span>
        </div>

        <h1
          style={{
            fontFamily: "-apple-system,system-ui,sans-serif",
            fontSize: 18,
            fontWeight: 800,
            color: "#fff",
            margin: "0 0 6px",
            lineHeight: 1.3,
          }}
        >
          {data.items.length === 1
            ? (data.items[0]?.title ?? "")
            : t("card.items_n", { n: data.items.reduce((s, it) => s + it.quantity, 0) })}
        </h1>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid rgba(255,255,255,.12)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 9,
                opacity: 0.55,
                fontFamily: "monospace",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {t("detail.receipt.total")}
            </div>
            <div
              style={{
                fontFamily: "-apple-system,system-ui,sans-serif",
                fontSize: 16,
                fontWeight: 900,
                color: "#fff",
              }}
            >
              {formatPrice(totalKopecks, displayLocale, displayCurrency)}
            </div>
          </div>
          {data.orderTime && (
            <div>
              <div
                style={{
                  fontSize: 9,
                  opacity: 0.55,
                  fontFamily: "monospace",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                {t("detail.receipt.pay_time")}
              </div>
              <div
                style={{
                  fontFamily: "-apple-system,system-ui,sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "rgba(255,255,255,.85)",
                }}
              >
                {(data.payTime ?? data.orderTime).slice(0, 10)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        {/* ── Photo check alert banner ────────────────────────────────────── */}
        {hasPhotoCheck && allCheckPhotos.length > 0 && (
          <div
            style={{
              background: "var(--color-sticker-yellow-soft,#FFF8DA)",
              border: "1px solid var(--color-sticker-yellow,#F5C842)",
              borderRadius: 10,
              padding: "10px 12px",
              fontSize: 11,
              color: "var(--color-sticker-yellow-fg,#6B4F00)",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 16 }}>📷</span>
            <div style={{ flex: 1 }}>
              <strong>
                {allCheckPhotos.length}{" "}
                {t("photo_check.banner")}
              </strong>
            </div>
          </div>
        )}

        {/* ── Progress stepper (only post-payment statuses) ───────────────── */}
        {cur > 0 && (
          <div className="tg-card">
            <h4 className="tg-card__h">
              {t("detail.section_progress")} ·{" "}
              {t("detail.section_progress_step", { cur, total: 6 })}
            </h4>
            <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
              {STEP_KEYS.map((step, idx) => {
                const done = step.num < cur;
                const active = step.num === cur;
                const last = idx === STEP_KEYS.length - 1;
                return (
                  <div key={step.key} style={{ display: "contents" }}>
                    <div
                      style={{
                        width: active ? 24 : 20,
                        height: active ? 24 : 20,
                        borderRadius: "50%",
                        background: done
                          ? "var(--color-primary-fill)"
                          : active
                            ? "var(--color-primary-soft)"
                            : "var(--color-bg,#fff)",
                        border: done
                          ? "2px solid var(--color-primary-fill)"
                          : active
                            ? "2px solid var(--color-primary-fill)"
                            : "2px solid var(--color-border)",
                        color: done
                          ? "#fff"
                          : active
                            ? "var(--color-primary-fill)"
                            : "var(--color-text-muted)",
                        display: "grid",
                        placeItems: "center",
                        fontSize: 10,
                        fontWeight: 800,
                        flexShrink: 0,
                      }}
                    >
                      {done ? "✓" : step.num}
                    </div>
                    {!last && (
                      <div
                        style={{
                          flex: 1,
                          height: 2,
                          background:
                            step.num < cur
                              ? "var(--color-primary-fill)"
                              : step.num === cur
                                ? "linear-gradient(90deg,var(--color-primary-fill),var(--color-border))"
                                : "var(--color-border)",
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 6,
                fontSize: 8,
                color: "var(--color-text-muted)",
                fontFamily: "monospace",
                letterSpacing: "0.04em",
              }}
            >
              {STEP_KEYS.map((step) => (
                <span key={step.key}>
                  {t(`detail.progress_steps.${step.key}` as never, step.key)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── QC photos grid ──────────────────────────────────────────────── */}
        {allCheckPhotos.length > 0 && (
          <div className="tg-card">
            <h4 className="tg-card__h">
              📷 {t("card.view_photos")} · {allCheckPhotos.length}
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6 }}>
              {allCheckPhotos.map((url, i) => (
                <div
                  key={`photo-${i}-${url.slice(-16)}`}
                  style={{ aspectRatio: "1", borderRadius: 8, overflow: "hidden" }}
                >
                  <img
                    src={url}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Items list ──────────────────────────────────────────────────── */}
        <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "10px 14px",
              background: "var(--color-surface)",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {t("detail.section_items")} ·{" "}
            {data.items.reduce((s, it) => s + it.quantity, 0)}{" "}
            {(t("detail.receipt.items").split("·")[0] ?? "").trim()}
          </div>
          {data.items.map((it, i) => {
            const canLink = Boolean(it.spuNo && it.channel);
            const inner = (
              <>
                <div className="tg-prod__img" style={{ background: "var(--color-surface)" }}>
                  {it.imageUrl ? (
                    <img
                      src={it.imageUrl}
                      alt={it.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }}
                    />
                  ) : (
                    "📦"
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="tg-prod__t">{it.title}</div>
                  <div className="tg-prod__s">
                    × {it.quantity}
                    {it.skuAttrEnumFormatList && it.skuAttrEnumFormatList.length > 0
                      ? ` · ${it.skuAttrEnumFormatList.map((a) => a.value).filter(Boolean).join(" / ")}`
                      : it.skuRemarks
                        ? ` · ${it.skuRemarks}`
                        : ""}
                  </div>
                </div>
                <div className="tg-prod__price">
                  {formatPrice(it.priceRubKopecks, displayLocale, displayCurrency)}
                </div>
              </>
            );
            const rowStyle = {
              borderBottom: i < data.items.length - 1 ? "1px solid var(--color-border)" : "none",
            };
            return canLink ? (
              <Link
                key={it.id}
                to={"/product/$channel/$spuNo" as never}
                params={{ channel: it.channel, spuNo: it.spuNo } as never}
                search={it.skuNo ? ({ sku: it.skuNo } as never) : ({} as never)}
                className="tg-prod"
                style={{ ...rowStyle, textDecoration: "none", color: "inherit" }}
              >
                {inner}
              </Link>
            ) : (
              <div key={it.id} className="tg-prod" style={rowStyle}>
                {inner}
              </div>
            );
          })}
        </div>

        {/* ── Address ─────────────────────────────────────────────────────── */}
        {data.address && (
          <div className="tg-card">
            <h4 className="tg-card__h">📍 {t("detail.section_address")}</h4>
            <div style={{ fontSize: 11, fontWeight: 700 }}>
              {data.address.receiverName} · {data.address.mobile}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "var(--color-text-muted)",
                marginTop: 3,
                lineHeight: 1.5,
              }}
            >
              {[
                data.address.country,
                data.address.province,
                data.address.city,
                data.address.district,
                data.address.detail,
              ]
                .filter(Boolean)
                .join(" · ")}
              {data.address.postCode && ` · ${data.address.postCode}`}
            </div>
          </div>
        )}

        {/* ── Receipt (financial summary) ─────────────────────────────────── */}
        <div className="tg-card">
          <h4 className="tg-card__h">{t("detail.section_receipt")}</h4>
          <Row
            k={t("detail.receipt.items", { n: data.items.reduce((s, it) => s + it.quantity, 0) })}
            v={formatPrice(data.itemAmountRubKopecks, displayLocale, displayCurrency)}
          />
          {data.expressFeeRubKopecks > 0 && (
            <Row
              k={t("detail.receipt.express")}
              v={formatPrice(data.expressFeeRubKopecks, displayLocale, displayCurrency)}
            />
          )}
          {data.valueServiceFeeRubKopecks > 0 && (
            <Row
              k={t("detail.receipt.value_service")}
              v={formatPrice(data.valueServiceFeeRubKopecks, displayLocale, displayCurrency)}
            />
          )}
          {data.serviceFeeRubKopecks > 0 && (
            <Row
              k={t("detail.receipt.service_fee")}
              v={formatPrice(data.serviceFeeRubKopecks, displayLocale, displayCurrency)}
            />
          )}
          {data.couponDiscountAmountRubKopecks > 0 && (
            <Row
              k={t("detail.receipt.coupon")}
              v={`− ${formatPrice(data.couponDiscountAmountRubKopecks, displayLocale, displayCurrency)}`}
              accent
            />
          )}
          {/* Payment method row (only when paid) */}
          {data.payment && (
            <Row
              k={t("detail.receipt.pay_method")}
              v={
                data.payment.channelName ??
                t(
                  `detail.receipt.pay_methods.${data.payment.method}` as never,
                  data.payment.method,
                )
              }
            />
          )}
          <div
            style={{
              borderTop: "2px solid var(--color-text)",
              marginTop: 10,
              paddingTop: 10,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <strong style={{ fontSize: 13 }}>{t("detail.receipt.total")}</strong>
            <strong
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 20,
                color: "var(--color-price,var(--color-cta))",
              }}
            >
              {formatPrice(totalKopecks, displayLocale, displayCurrency)}
            </strong>
          </div>
        </div>

        {/* ── Domestic track ──────────────────────────────────────────────── */}
        <MiniDomesticTrack items={data.items} authToken={authToken} />
      </div>

      {/* ── Bottom action bar ────────────────────────────────────────────── */}
      {(canPay || canCancel) && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            borderTop: "1px solid var(--color-border)",
            padding: "8px 12px",
            display: "flex",
            gap: 8,
            zIndex: 10,
          }}
        >
          {canCancel && (
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              style={{
                flex: 1,
                background: "#fff",
                border: "1.5px solid var(--color-border-strong)",
                padding: 11,
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 12,
                color: "var(--color-text)",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {t("cancel.action")}
            </button>
          )}
          {canPay && (
            <button
              type="button"
              onClick={() => setShowCashier(true)}
              style={{
                flex: 1,
                background: "var(--color-cta)",
                color: "#fff",
                border: 0,
                padding: 11,
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {t("detail.go_to_pay")}
            </button>
          )}
        </div>
      )}

      {/* ── Cancel order modal ───────────────────────────────────────────── */}
      {cancelOpen && (
        <CancelOrderModal
          orderNo={data.orderNo}
          authToken={authToken}
          onClose={() => setCancelOpen(false)}
        />
      )}
    </TgPage>
  );
}

// ─── Cancel order modal ───────────────────────────────────────────────────────

function CancelOrderModal({
  orderNo,
  authToken,
  onClose,
}: {
  orderNo: string;
  authToken: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [otherText, setOtherText] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const cancelMut = useCancelOrder(authToken);

  const isOther = reason === "reason_other";
  const finalReason = isOther ? otherText.trim() : reason;

  const submit = () => {
    if (!finalReason) return;
    setError("");
    cancelMut.mutate(
      { orderNo, cancelReason: finalReason },
      {
        onSuccess: () => {
          setDone(true);
          setTimeout(() => {
            navigate({ to: "/orders" as never });
          }, 1200);
        },
        onError: () => {
          setError(t("cancel.failed"));
        },
      },
    );
  };

  return (
    /* Overlay */
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "16px 16px 0 0",
          padding: "16px 16px 32px",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        {/* Handle bar */}
        <div
          style={{
            width: 36,
            height: 4,
            background: "var(--color-border-strong)",
            borderRadius: 2,
            margin: "0 auto 16px",
          }}
        />

        {done ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 32 }}>✅</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 10 }}>
              {t("cancel.success")}
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>
              {t("cancel.confirm_title")}
            </div>
            <div
              style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 16, lineHeight: 1.5 }}
            >
              {t("cancel.confirm_body")}
            </div>

            {/* Reason list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {CANCEL_REASON_KEYS.map((key) => (
                <label
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: `1.5px solid ${reason === key ? "var(--color-primary-fill)" : "var(--color-border)"}`,
                    background:
                      reason === key ? "var(--color-primary-soft)" : "var(--color-bg,#fff)",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: reason === key ? 700 : 400,
                  }}
                >
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={key}
                    checked={reason === key}
                    onChange={() => setReason(key)}
                    style={{ display: "none" }}
                  />
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: `2px solid ${reason === key ? "var(--color-primary-fill)" : "var(--color-border-strong)"}`,
                      background: reason === key ? "var(--color-primary-fill)" : "transparent",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {reason === key && (
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: "#fff",
                        }}
                      />
                    )}
                  </span>
                  {t(`cancel.${key}` as never, key)}
                </label>
              ))}
            </div>

            {/* Other reason text input */}
            {isOther && (
              <textarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder={t("cancel.reason_other_placeholder")}
                style={{
                  marginTop: 10,
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1.5px solid var(--color-border)",
                  fontSize: 13,
                  lineHeight: 1.5,
                  minHeight: 72,
                  resize: "none",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            )}

            {error && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: "var(--color-danger,#d9304e)",
                  textAlign: "center",
                }}
              >
                {error}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1,
                  background: "#fff",
                  border: "1.5px solid var(--color-border-strong)",
                  padding: 11,
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 12,
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {t("cancel.keep")}
              </button>
              <button
                type="button"
                disabled={!finalReason || cancelMut.isPending}
                onClick={submit}
                style={{
                  flex: 1,
                  background:
                    !finalReason || cancelMut.isPending
                      ? "var(--color-border)"
                      : "var(--color-danger,#d9304e)",
                  color: "#fff",
                  border: 0,
                  padding: 11,
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: !finalReason || cancelMut.isPending ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                {cancelMut.isPending
                  ? t("cancel.submitting")
                  : t("cancel.submit")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Status dictionary ────────────────────────────────────────────────────────

const KNOWN_STATUS = new Set([
  "collected",
  "awaiting_pickup",
  "in_transit",
  "arrived_warehouse",
  "handed_intl",
  "ordered",
  "out_for_delivery",
  "at_pickup_point",
  "generic",
]);

function formatTrackTime(raw: string): string {
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return raw;
  const [, , mm, dd, hh, min] = m;
  return `${mm}-${dd} ${hh}:${min}`;
}

// ─── Mini domestic track section ─────────────────────────────────────────────

/**
 * 国内采购物流轨迹 (mini). One collapsible waybill block per express number.
 * Default view: aggregated phase nodes (≈6, no truncation).
 * Toggle: "Все события (N)" reveals the full raw steps.
 */
function MiniDomesticTrack({
  items,
  authToken,
}: {
  items: OrderItem[];
  authToken: string | null;
}) {
  const { t } = useTranslation("orders");
  const waybills = items.flatMap((it) =>
    (it.expressNoList ?? []).map((expressNo) => ({ item: it, expressNo })),
  );
  if (waybills.length === 0) return null;

  return (
    <div className="tg-card">
      <h4 className="tg-card__h">{t("detail.domestic_track.title")}</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {waybills.map(({ item, expressNo }) => (
          <MiniWaybillBlock
            key={`${item.id}-${expressNo}`}
            item={item}
            expressNo={expressNo}
            authToken={authToken}
          />
        ))}
      </div>
    </div>
  );
}

function MiniWaybillBlock({
  item,
  expressNo,
  authToken,
}: {
  item: OrderItem;
  expressNo: string;
  authToken: string | null;
}) {
  const { t } = useTranslation("orders");
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, refetch } = useDomesticTrack(authToken, expressNo, open);

  const allSteps = (data ?? []).flatMap((trk) => trk.steps);
  const aggregated =
    (data ?? []).find((trk) => trk.aggregatedSteps != null)?.aggregatedSteps ?? null;

  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          padding: "8px 10px",
          background: "var(--color-surface)",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 700,
              color: "var(--color-text)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.title}
          </span>
          <span
            style={{
              display: "block",
              fontSize: 10,
              fontFamily: "var(--font-mono)",
              color: "var(--color-text-muted)",
              marginTop: 2,
            }}
          >
            {t("detail.domestic_track.express_no")} · {expressNo}
          </span>
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-primary-fill)" }}>
          {open
            ? t("detail.domestic_track.btn_collapse")
            : t("detail.domestic_track.btn_detail")}
        </span>
      </button>

      {open && (
        <div style={{ padding: "10px" }}>
          {isLoading && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
              {t("detail.domestic_track.loading")}
            </div>
          )}
          {isError && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
              {t("detail.domestic_track.error")}{" "}
              <button
                type="button"
                onClick={() => refetch()}
                style={{
                  background: "none",
                  border: "1px solid var(--color-border)",
                  borderRadius: 6,
                  padding: "2px 8px",
                  fontSize: 10,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  color: "var(--color-text)",
                }}
              >
                {t("detail.domestic_track.retry")}
              </button>
            </div>
          )}
          {!isLoading && !isError && allSteps.length === 0 && (aggregated == null || aggregated.length === 0) && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
              {t("detail.domestic_track.no_data")}
            </div>
          )}
          {!isLoading && !isError && (allSteps.length > 0 || (aggregated != null && aggregated.length > 0)) && (
            <MiniTimeline aggregatedSteps={aggregated} rawSteps={allSteps} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Two-mode mini timeline.
 * Default: aggregated phase nodes (all shown, no truncation).
 * Toggle: "Все события (N)" / "Свернуть" reveals the full raw step list.
 */
function MiniTimeline({
  aggregatedSteps,
  rawSteps,
}: {
  aggregatedSteps: AggregatedTrackStep[] | null;
  rawSteps: DomesticTrackStep[];
}) {
  const { t } = useTranslation("orders");
  const [showAll, setShowAll] = useState(false);
  const hasAgg = aggregatedSteps != null && aggregatedSteps.length > 0;
  // Always de-duplicated phases; never the raw 16-row noise. Collapsed = latest 3.
  const DEFAULT_VISIBLE = 3;
  const total = hasAgg ? aggregatedSteps!.length : rawSteps.length;
  // Expand only for de-duplicated aggregated phases; the raw fallback stays capped at
  // 3 (expanding raw would dump the 16-row scan noise this feature exists to kill).
  const visibleCount = hasAgg && showAll ? total : Math.min(DEFAULT_VISIBLE, total);

  return (
    <>
      <div style={{ position: "relative", paddingLeft: 22 }}>
        <span
          style={{
            position: "absolute",
            left: 6,
            top: 6,
            bottom: 18,
            width: 2,
            background: "var(--color-border)",
          }}
        />
        {hasAgg
          ? aggregatedSteps!.slice(0, visibleCount).map((step, idx) => (
              <MiniAggRow
                key={`agg-${step.time}-${idx}`}
                step={step}
                isLatest={idx === 0}
                last={idx === visibleCount - 1}
              />
            ))
          : rawSteps.slice(0, visibleCount).map((step, idx) => (
              <MiniRawRow
                key={`raw-${step.time || step.acceptTime}-${idx}`}
                step={step}
                isLatest={idx === 0}
                last={idx === visibleCount - 1}
              />
            ))}
      </div>

      {hasAgg && total > DEFAULT_VISIBLE && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          style={{
            marginTop: 6,
            width: "100%",
            padding: 8,
            background: "var(--color-surface)",
            border: 0,
            borderRadius: 10,
            fontFamily: "inherit",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--color-text)",
            cursor: "pointer",
          }}
        >
          {showAll
            ? t("detail.domestic_track.btn_collapse")
            : t("tracking.show_full").replace("{n}", String(total))}
        </button>
      )}
    </>
  );
}

// ─── Aggregated event row (mini) ──────────────────────────────────────────────

function MiniAggRow({
  step,
  isLatest,
  last,
}: {
  step: AggregatedTrackStep;
  isLatest: boolean;
  last: boolean;
}) {
  const { t } = useTranslation("orders");
  const isKnown = KNOWN_STATUS.has(step.status) && step.status !== "generic";
  const statusLabel = isKnown
    ? t(`tracking.status.${step.status}`, step.status)
    : (step.cleanedText?.trim() || t("tracking.status.generic"));
  const time = step.time ? formatTrackTime(step.time) : "";
  const locLine = [step.location, step.carrier].filter(Boolean).join(" · ");

  return (
    <div style={{ position: "relative", paddingBottom: last ? 0 : 14, fontSize: 11 }}>
      <span
        style={{
          position: "absolute",
          left: -18,
          top: 2,
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: isLatest ? "var(--color-primary-fill)" : "#fff",
          border: isLatest
            ? "2px solid var(--color-primary-fill)"
            : "2px solid var(--color-border-strong)",
          boxShadow: isLatest ? "0 0 0 3px var(--color-primary-soft)" : "none",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: isLatest ? "var(--color-primary-fill)" : "var(--color-text)",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {statusLabel}
          {step.count > 1 && (
            <span
              style={{
                marginLeft: 5,
                fontSize: 10,
                fontWeight: 600,
                color: "var(--color-text-muted)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {t("tracking.merged_count").replace("{n}", String(step.count))}
            </span>
          )}
        </span>
        {time && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--color-text-muted)",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {time}
          </span>
        )}
      </div>
      {locLine && (
        <div
          style={{
            fontSize: 11,
            color: "var(--color-text-muted)",
            marginTop: 3,
            display: "flex",
            alignItems: "center",
            gap: 5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "var(--color-primary-fill)", flexShrink: 0 }}>●</span> {locLine}
        </div>
      )}
    </div>
  );
}

// ─── Raw event row (mini) ─────────────────────────────────────────────────────

function MiniRawRow({
  step,
  isLatest,
  last,
}: {
  step: DomesticTrackStep;
  isLatest: boolean;
  last: boolean;
}) {
  const { t } = useTranslation("orders");
  const [rawOpen, setRawOpen] = useState(false);

  const isKnown = KNOWN_STATUS.has(step.status) && step.status !== "generic";
  const statusLabel = isKnown
    ? t(`tracking.status.${step.status}`, step.status)
    : (step.cleanedText?.trim() || t("tracking.status.generic"));
  const time = formatTrackTime(step.time || step.acceptTime);
  const locLine = [step.location, step.carrier].filter(Boolean).join(" · ");
  const hasRaw = step.raw.trim() !== "";

  return (
    <div style={{ position: "relative", paddingBottom: last ? 0 : 14, fontSize: 11 }}>
      <span
        style={{
          position: "absolute",
          left: -18,
          top: 2,
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: isLatest ? "var(--color-primary-fill)" : "#fff",
          border: isLatest
            ? "2px solid var(--color-primary-fill)"
            : "2px solid var(--color-border-strong)",
          boxShadow: isLatest ? "0 0 0 3px var(--color-primary-soft)" : "none",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: isLatest ? "var(--color-primary-fill)" : "var(--color-text)",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {statusLabel}
        </span>
        {time && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--color-text-muted)",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {time}
          </span>
        )}
      </div>
      {locLine && (
        <div
          style={{
            fontSize: 11,
            color: "var(--color-text-muted)",
            marginTop: 3,
            display: "flex",
            alignItems: "center",
            gap: 5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "var(--color-primary-fill)", flexShrink: 0 }}>●</span> {locLine}
        </div>
      )}
      {hasRaw && (
        <>
          <button
            type="button"
            onClick={() => setRawOpen((v) => !v)}
            style={{
              marginTop: 5,
              fontSize: 11,
              color: "var(--color-text-muted)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "none",
              border: 0,
              padding: 0,
              fontFamily: "inherit",
            }}
          >
            {t("tracking.carrier_detail")}
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ transform: rawOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {rawOpen && (
            <div
              style={{
                marginTop: 6,
                padding: "8px 10px",
                background: "var(--color-surface)",
                borderRadius: 8,
                fontSize: 11,
                lineHeight: 1.6,
                color: "var(--color-text-muted)",
              }}
            >
              {step.raw}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Row({ k, v, accent }: { k: string; v: string; accent?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "6px 0",
        fontSize: 12,
      }}
    >
      <span style={{ color: "var(--color-text-muted)" }}>{k}</span>
      <span
        style={{
          fontWeight: 600,
          color: accent ? "var(--color-primary-fill)" : "var(--color-text)",
        }}
      >
        {v}
      </span>
    </div>
  );
}
